import { DollarSign } from "lucide-react";
import {
  Create,
  Datagrid,
  DateField,
  DateTimeInput,
  BooleanInput,
  Edit,
  EmailField,
  FormDataConsumer,
  FunctionField,
  List,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  required,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
} from "react-admin";
import { Alert, Box } from "@mui/material";
import { CardListActions, EditorToolbar } from "../../components";

const sourceChoices = [
  { id: "double_recurring", name: "Managed by Double" },
  { id: "stripe_recurring", name: "Verified Stripe recurring" },
  { id: "one_time_annual", name: "One-time annual membership" },
  { id: "manual", name: "Approved manual access" },
  { id: "legacy_unclassified", name: "Needs manual review" },
  { id: "legacy_ineligible", name: "Reviewed — not eligible" },
];

const manualReviewResolutionChoices = [
  { id: "unresolved", name: "Still unresolved — save research notes" },
  { id: "manual", name: "Keep access — approved manual membership" },
  {
    id: "stripe_recurring",
    name: "Keep access — verified Stripe subscription",
  },
  {
    id: "one_time_annual",
    name: "Keep access — verified one-time annual membership",
  },
  { id: "end_not_eligible", name: "End access — not eligible" },
  { id: "end_duplicate", name: "End access — duplicate row" },
];

function providerStatus(record) {
  const status = record?.provider_status;
  if (!status) return "Not applicable";
  if (status === "1stChargeSuccess") return `Active (${status})`;
  if (
    ["paymentMethodSuccess", "pending", "subscriptionScheduled"].includes(
      status,
    )
  ) {
    return `Scheduled/setup (${status})`;
  }
  if (status === "endedCanceled") return `Canceled (${status})`;
  if (status === "endedError") return `Ended after error (${status})`;
  if (status === "endedSuccess") return `Completed (${status})`;
  if (status.toLowerCase().includes("error")) {
    return `Payment/setup problem (${status})`;
  }
  return `Does not support recurring access (${status})`;
}

function ManualReviewFields() {
  return (
    <>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Search the billing email in Double and Stripe, inspect the recurring
        plan and payment history, then select an outcome below. Put the evidence
        you found in “Reason for This Change.”
      </Alert>
      <SelectInput
        source="manual_review_resolution"
        label="Manual Review Outcome"
        choices={manualReviewResolutionChoices}
        defaultValue="unresolved"
        validate={required()}
        fullWidth
      />
      {/* Only verified provider-backed outcomes need a stable provider ID. */}
      <FormDataConsumer>
        {({ formData }) =>
          ["stripe_recurring", "one_time_annual"].includes(
            formData.manual_review_resolution,
          ) && (
            <TextInput
              source="manual_review_external_id"
              label="Stripe Subscription or Payment ID"
              validate={required()}
              fullWidth
            />
          )
        }
      </FormDataConsumer>
      <FormDataConsumer>
        {({ formData }) =>
          formData.manual_review_resolution === "one_time_annual" && (
            <DateTimeInput
              source="manual_review_payment_date"
              label="Verified Stripe Payment Date"
              validate={required()}
              helperText="Access will run for exactly one year from this payment."
            />
          )
        }
      </FormDataConsumer>
      <FormDataConsumer>
        {({ formData }) =>
          formData.manual_review_resolution === "manual" && (
            <BooleanInput
              source="indefinite_access_confirmed"
              label="I confirm this manual access is intentionally indefinite"
              helperText="Required when the current row has no end date."
            />
          )
        }
      </FormDataConsumer>
    </>
  );
}

function SubscriptionFields({ creating = false }) {
  const record = useRecordContext();
  const manual = creating || record?.source_type === "manual";

  return (
    <>
      <TextInput
        source="billing_email"
        label="Billing Email"
        type="email"
        disabled={!manual}
      />
      <TextInput source="ta_email" label="TA Email" type="email" />
      <TextInput source="name" label="Name" />
      <TextInput source="source_type" label="Source" disabled />
      <TextInput
        source="source_external_id"
        label="Provider Plan ID"
        disabled
      />
      <TextInput source="provider_status" label="Provider Status" disabled />
      <TextInput source="provenance_label" label="Provenance Label" disabled />
      <TextInput
        source="provenance_note"
        label="Provenance / Prior Research"
        multiline
        disabled
        fullWidth
      />
      {!creating && (
        <>
          <TextInput source="frequency" label="Frequency" disabled />
          <TextInput source="amount" label="Base Amount" disabled />
          <TextInput source="gross_amount" label="Gross Amount" disabled />
          <TextInput source="currency" label="Currency" disabled />
          <TextInput source="amount_usd" label="USD Value" disabled />
          <TextInput source="ended_reason" label="Ended Reason" disabled />
        </>
      )}
      <DateTimeInput
        source="started_at"
        label="Started At"
        disabled={!manual}
      />
      <DateTimeInput source="ended_at" label="Ended At" disabled={!manual} />
      {manual && (
        <>
          <BooleanInput
            source="indefinite_access_confirmed"
            label="I confirm this access is intentionally indefinite"
            helperText="Required when creating access without an end date, or when removing an existing end date."
          />
          <TextInput
            source="manual_reason"
            label="Manual Access Justification"
            multiline
            validate={creating ? required() : undefined}
            helperText="Required for every new manual grant."
          />
        </>
      )}
      {!creating && record?.source_type === "legacy_unclassified" && (
        <ManualReviewFields />
      )}
      {!creating && (
        <TextInput
          source="audit_reason"
          label="Reason for This Change"
          multiline
          validate={required()}
          helperText="Describe what you verified and where. Stored in the append-only access audit log."
          fullWidth
        />
      )}
    </>
  );
}

function renderEditForm(creating = false) {
  return (
    <SimpleForm toolbar={<EditorToolbar />}>
      <SubscriptionFields creating={creating} />
    </SimpleForm>
  );
}

function CreateView(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  return (
    <Create
      {...props}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Subscription created successfully!", {
            type: "success",
          });
          refresh();
          redirect("list", "dollar-a-day-subscriptions");
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      {renderEditForm(true)}
    </Create>
  );
}

function EditView(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  return (
    <Edit
      {...props}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Subscription updated successfully!", {
            type: "success",
          });
          refresh();
          redirect(false);
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      {renderEditForm()}
    </Edit>
  );
}

function ListView(props) {
  return (
    <List
      {...props}
      actions={<CardListActions exporter={true} />}
      filters={[
        <TextInput source="id" alwaysOn />,
        <TextInput label="Billing Email" source="billing_email" alwaysOn />,
        <TextInput label="TA Email" source="ta_email" alwaysOn />,
        <TextInput label="Name" source="name" alwaysOn />,
        <SelectInput
          label="Source"
          source="source_type"
          choices={sourceChoices}
        />,
        <TextInput label="Discrepancy Group" source="provenance_label" />,
      ]}
      perPage={25}
      sort={{ field: "id", order: "DESC" }}
      title="Dollar-a-Day Subscriptions"
    >
      <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          For Double rows, Active and Scheduled/setup support access. Canceled,
          completed, error, and unknown statuses do not. Started/Ended dates
          determine whether the recorded access is currently effective. Filter
          Source to “Needs manual review” to work the discrepancy queue.
        </Alert>
        <Datagrid rowClick="edit" bulkActionButtons={false}>
          <TextField source="id" label="Id" />
          <EmailField source="billing_email" label="Billing Email" />
          <EmailField source="ta_email" label="TA Email" />
          <TextField source="name" label="Name" />
          <FunctionField
            label="Source"
            render={(record) =>
              sourceChoices.find(({ id }) => id === record.source_type)?.name ||
              record.source_type
            }
          />
          <FunctionField label="Provider Status" render={providerStatus} />
          <DateField source="started_at" label="Started At" showTime />
          <DateField source="ended_at" label="Ended At" showTime />
        </Datagrid>
      </Box>
    </List>
  );
}

export const DollarADaySubscriptions = {
  category: "misc",
  name: "dollar-a-day-subscriptions",
  create: CreateView,
  edit: EditView,
  icon: DollarSign,
  list: ListView,
  options: { label: "Dollar-a-Day Subscriptions" },
};
